/**
 * §P.3 · El catálogo de descuentos del club.
 *
 * Cuatro cosas que esta pantalla hace a propósito y que no se ven en el código:
 *
 * **El código no se edita.** El formulario de edición no lo enseña como campo, y
 * lo dice: el código aparece en el desglose de la cuota, así que cambiarlo
 * reescribiría lo que se lee de recibos ya emitidos. Y choca **con cualquier
 * regla, activa o retirada** (`409 discount_code_taken`), al revés que el nombre
 * de una tarifa.
 *
 * **«Retirar», no «Eliminar»**, y aquí el verbo es un `PATCH active: false`: deja
 * de poder darse a nadie nuevo y **quien ya lo tiene lo conserva**. Es lo que un
 * club quiere decir al retirar una promoción, así que se dice con esas palabras.
 *
 * **`in_use` cuenta los vigentes hoy.** Una aplicación que terminó en junio no
 * cuenta, y es la cifra que le dice al club si retirar la regla deja a alguien
 * colgado.
 *
 * **El aviso de caducidad va arriba.** `/discounts/expiring` existe para que el
 * club se entere antes que la familia, y un aviso al final de la página no se ve.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import {
  AMBITOS,
  aTextoDeValor,
  aValorDeRegla,
  etiquetaDeAmbito,
  etiquetaDeValor,
  type AmbitoDeDescuento,
  type ReglaDeDescuento,
  type TipoDeDescuento,
} from '@/club/descuentos/tipos';
import { AvisoDeCaducidad } from '@/club/descuentos/AvisoDeCaducidad';

interface Campos {
  code: string;
  name: string;
  kind: TipoDeDescuento;
  valor: string;
  scope: AmbitoDeDescuento;
  applies_from_nth: string;
}

const desde = (r: ReglaDeDescuento | null): Campos => ({
  code: r?.code ?? '',
  name: r?.name ?? '',
  kind: r?.kind ?? 'percent',
  valor: r ? aTextoDeValor(r.value) : '',
  scope: (r?.scope as AmbitoDeDescuento) ?? 'manual',
  applies_from_nth: r?.applies_from_nth?.toString() ?? '',
});

export function CatalogoDeDescuentos({
  club,
  puedeEditar,
  alAbrirAlumno,
}: {
  club: string;
  puedeEditar: boolean;
  /** Del aviso de caducidad a la ficha del alumno: es la llamada que hay que hacer. */
  alAbrirAlumno: (alumnoId: string) => void;
}) {
  const cache = useQueryClient();
  const [conRetiradas, setConRetiradas] = useState(false);
  const [editando, setEditando] = useState<ReglaDeDescuento | null | undefined>(
    undefined
  );
  const [campos, setCampos] = useState<Campos>(() => desde(null));
  const [problema, setProblema] = useState<Problema | null>(null);

  const reglas = useQuery({
    queryKey: ['descuentos', club, conRetiradas],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        ['discount-rules'].get({
          query: conRetiradas ? { inactive: true } : {},
        });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as ReglaDeDescuento[];
    },
  });

  const abrir = (r: ReglaDeDescuento | null) => {
    setEditando(r);
    setCampos(desde(r));
    setProblema(null);
  };

  const cerrar = () => setEditando(undefined);

  const guardar = useMutation({
    mutationFn: async () => {
      const valor = aValorDeRegla(campos.valor, campos.kind);
      if (valor === null) {
        throw new ErrorDeApi({
          title: 'El valor no se entiende',
          status: 400,
          detail:
            campos.kind === 'percent'
              ? 'Un porcentaje entre 0 y 100: 10 o 12,5.'
              : 'En euros, con dos decimales como mucho: 5 o 5,50.',
          errors: [{ field: 'value', message: 'No es un valor válido' }],
        });
      }

      const nth = Number.parseInt(campos.applies_from_nth.trim(), 10);
      const desdeElHijo =
        campos.scope === 'sibling' && Number.isFinite(nth) ? nth : null;

      if (editando === null) {
        const r = await api.v1.clubs({ club })['discount-rules'].post({
          code: campos.code.trim().toUpperCase(),
          name: campos.name.trim(),
          kind: campos.kind,
          value: valor,
          scope: campos.scope,
          applies_from_nth: desdeElHijo,
        });
        if (r.error) throw new ErrorDeApi(aProblema(r.error));
        return r.data as unknown as ReglaDeDescuento;
      }

      // Sin `code` ni `kind`: el `PATCH` no los acepta y no es un olvido. El
      // código se lee en recibos ya emitidos, y cambiarle el tipo a una regla
      // viva convertiría un 10 % en 10 € en la cuota de quien la tenga.
      const r = await api.v1
        .clubs({ club })
        ['discount-rules']({ ruleId: editando!.id })
        .patch({
          name: campos.name.trim(),
          value: valor,
          applies_from_nth: desdeElHijo,
        });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as ReglaDeDescuento;
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['descuentos', club] });
      cerrar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const retirar = useMutation({
    mutationFn: async (regla: ReglaDeDescuento) => {
      const r = await api.v1
        .clubs({ club })
        ['discount-rules']({ ruleId: regla.id })
        .patch({ active: !regla.active });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () =>
      void cache.invalidateQueries({ queryKey: ['descuentos', club] }),
    onError: (e) => setProblema(aProblema(e)),
  });

  // ── Formulario ───────────────────────────────────────────────────────────
  if (editando !== undefined) {
    const cambiar = (campo: keyof Campos, valor: string) =>
      setCampos((c) => ({ ...c, [campo]: valor }));

    const ambito = AMBITOS.find((a) => a.valor === campos.scope);

    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          guardar.mutate();
        }}
      >
        <h1>{editando ? 'Editar descuento' : 'Nuevo descuento'}</h1>

        <div className="rejilla">
          {editando ? (
            <div className="campo-fijo">
              <span className="campo-fijo-etiqueta">Código</span>
              <code>{editando.code}</code>
            </div>
          ) : (
            <label>
              Código
              <input
                required
                minLength={2}
                maxLength={40}
                placeholder="HERMANOS"
                value={campos.code}
                onChange={(e) => cambiar('code', e.target.value.toUpperCase())}
              />
            </label>
          )}

          <label>
            Nombre
            <input
              required
              minLength={2}
              maxLength={120}
              placeholder="Segundo hermano"
              value={campos.name}
              onChange={(e) => cambiar('name', e.target.value)}
            />
          </label>

          {editando ? (
            <div className="campo-fijo">
              <span className="campo-fijo-etiqueta">Tipo</span>
              <span>
                {editando.kind === 'percent' ? 'Porcentaje' : 'Euros'}
              </span>
            </div>
          ) : (
            <label>
              Tipo
              <select
                value={campos.kind}
                onChange={(e) => cambiar('kind', e.target.value)}
              >
                <option value="percent">Un porcentaje</option>
                <option value="fixed">Unos euros</option>
              </select>
            </label>
          )}

          <label>
            {campos.kind === 'percent' ? 'Porcentaje' : 'Euros que se quitan'}
            {/*
              El `fixed` va en euros y no en céntimos: es la única cifra de dinero
              del proyecto que no va en céntimos, porque la columna es
              `numeric(7,2)` y la comparte con el porcentaje.
            */}
            <input
              required
              inputMode="decimal"
              placeholder={campos.kind === 'percent' ? '10' : '5,00'}
              value={campos.valor}
              onChange={(e) => cambiar('valor', e.target.value)}
            />
          </label>

          {!editando && (
            <label>
              Cuándo se aplica
              <select
                value={campos.scope}
                onChange={(e) => cambiar('scope', e.target.value)}
              >
                {AMBITOS.map((a) => (
                  <option key={a.valor} value={a.valor}>
                    {a.etiqueta}
                  </option>
                ))}
              </select>
            </label>
          )}

          {campos.scope === 'sibling' && (
            <label>
              A partir de qué hijo
              <input
                type="number"
                min={1}
                max={10}
                placeholder="2"
                value={campos.applies_from_nth}
                onChange={(e) => cambiar('applies_from_nth', e.target.value)}
              />
            </label>
          )}
        </div>

        {ambito && !editando && <p className="tenue">{ambito.explicacion}</p>}

        {editando ? (
          <div className="nota">
            <strong>Cambiar el valor no toca lo ya facturado.</strong> Cada
            línea de recibo guarda su importe copiado al emitirse; lo que cambia
            es el próximo recibo de quien lo tenga aplicado.
          </div>
        ) : (
          <div className="nota">
            <strong>El código no se podrá cambiar.</strong> Aparece en el
            desglose de la cuota, y cambiarlo reescribiría lo que se lee de
            recibos ya emitidos.
          </div>
        )}

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button type="submit" disabled={guardar.isPending}>
            {guardar.isPending
              ? 'Guardando…'
              : editando
                ? 'Guardar el descuento'
                : 'Crear el descuento'}
          </button>
          <button
            type="button"
            className="btn-plano"
            onClick={cerrar}
            disabled={guardar.isPending}
          >
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  // ── Lista ────────────────────────────────────────────────────────────────
  return (
    <section>
      <header className="titulo-fila">
        <h1>Descuentos</h1>
        {puedeEditar && (
          <button type="button" onClick={() => abrir(null)}>
            Nuevo descuento
          </button>
        )}
      </header>

      <p className="tenue">
        El catálogo del club. Un descuento se crea aquí y se aplica desde la
        cuota de cada alumno, con o sin fecha de fin.
      </p>

      {/* Arriba a propósito: es el aviso que tiene que llegar antes que la familia. */}
      <AvisoDeCaducidad club={club} alAbrirAlumno={alAbrirAlumno} />

      <label className="interruptor">
        <input
          type="checkbox"
          checked={conRetiradas}
          onChange={(e) => setConRetiradas(e.target.checked)}
        />
        Enseñar también los retirados
      </label>

      {reglas.isPending && (
        <Cargando filas={3} etiqueta="Cargando descuentos" />
      )}

      {reglas.isError && (
        <PanelError
          problema={aProblema(reglas.error)}
          reintentar={() => void reglas.refetch()}
        />
      )}

      {problema && <PanelError problema={problema} />}

      {reglas.data?.length === 0 && (
        <Vacio
          titulo="Todavía no hay descuentos"
          explicacion="Un descuento es una rebaja con nombre: «Segundo hermano, 10 %». Se guarda aparte del precio para que el club pueda explicar por qué una familia paga menos, y para acordarse de volver a subirle cuando caduque."
          {...(puedeEditar
            ? {
                accion: (
                  <button type="button" onClick={() => abrir(null)}>
                    Crear el primer descuento
                  </button>
                ),
              }
            : {})}
        />
      )}

      {reglas.data && reglas.data.length > 0 && (
        <div className="tabla-marco">
          <table className="tabla">
            <thead>
              <tr>
                <th>Descuento</th>
                <th>Código</th>
                <th className="num">Rebaja</th>
                <th>Cuándo</th>
                <th className="num">En uso</th>
                {puedeEditar && <th />}
              </tr>
            </thead>
            <tbody>
              {reglas.data.map((r) => (
                <tr key={r.id} className={r.active ? undefined : 'fila-fuera'}>
                  <td>
                    {r.name}
                    {!r.active && (
                      <span className="pastilla pastilla-left">Retirado</span>
                    )}
                  </td>
                  <td>
                    <code>{r.code}</code>
                  </td>
                  <td className="num">{etiquetaDeValor(r.kind, r.value)}</td>
                  <td className="tenue">
                    {etiquetaDeAmbito(r.scope)}
                    {r.scope === 'sibling' && r.applies_from_nth
                      ? ` · desde el ${r.applies_from_nth}.º`
                      : ''}
                  </td>
                  {/* Vigentes hoy, no históricos: es lo que decide si retirarlo deja a alguien colgado. */}
                  <td className="num tenue">
                    {r.in_use === 0 ? '—' : r.in_use}
                  </td>
                  {puedeEditar && (
                    <td>
                      <div className="tutor-acciones" style={{ margin: 0 }}>
                        <button
                          type="button"
                          className="btn-plano"
                          onClick={() => abrir(r)}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          className="btn-plano"
                          disabled={retirar.isPending}
                          onClick={() => retirar.mutate(r)}
                        >
                          {r.active ? 'Retirar' : 'Reactivar'}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {reglas.data && reglas.data.length > 0 && (
        <>
          <p className="tenue">
            Las rebajas <strong>se acumulan</strong>: un segundo hijo que además
            entró con la promoción de septiembre tiene las dos, y el desglose de
            su cuota dice de dónde sale cada resta. Total de hoy en la ficha del
            alumno.
          </p>
          <p className="tenue">
            Retirar uno lo saca de esta lista y{' '}
            <strong>no se lo quita a quien ya lo tiene</strong>: sigue
            aplicándose en su cuota hasta que alguien lo quite allí. Se vuelven
            a ver con el interruptor de arriba.
          </p>
        </>
      )}
    </section>
  );
}
